import api from './api';

/**
 * Pet Service - Handles all pet-related API calls
 */

const petService = {
  /**
   * Fetch all animal categories
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchAnimalCategories() {
    try {
      const response = await api.get('/pets/categories/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch animal categories'
      };
    }
  },

  /**
   * Fetch breeds, optionally filtered by animal category
   * @param {number} categoryId - Optional animal category ID to filter
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchBreeds(categoryId = null) {
    try {
      const params = categoryId ? { animal_category: categoryId } : {};
      const response = await api.get('/pets/breeds/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch breeds'
      };
    }
  },

  /**
   * Fetch user's pets
   * @param {Object} params - Query parameters for filtering/sorting
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchMyPets(params = {}) {
    try {
      const response = await api.get('/pets/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch pets'
      };
    }
  },

  /**
   * Fetch single pet detail
   * @param {number} petId - Pet ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchPetDetail(petId) {
    try {
      const response = await api.get(`/pets/${petId}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch pet details'
      };
    }
  },

  /**
   * Create a new pet
   * @param {Object} petData - Pet data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async createPet(petData) {
    try {
      // If photo is included, send as multipart/form-data
      let response;
      if (petData.photo && petData.photo instanceof File) {
        const formData = new FormData();
        Object.keys(petData).forEach(key => {
          if (petData[key] !== null && petData[key] !== undefined && petData[key] !== '') {
            formData.append(key, petData[key]);
          }
        });
        response = await api.post('/pets/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Send as JSON
        const cleanedData = { ...petData };
        delete cleanedData.photo;
        response = await api.post('/pets/', cleanedData);
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message || 'Failed to create pet'
      };
    }
  },

  /**
   * Update an existing pet
   * @param {number} petId - Pet ID
   * @param {Object} petData - Updated pet data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async updatePet(petId, petData) {
    try {
      // If photo is included, send as multipart/form-data
      let response;
      if (petData.photo && petData.photo instanceof File) {
        const formData = new FormData();
        Object.keys(petData).forEach(key => {
          if (petData[key] !== null && petData[key] !== undefined && petData[key] !== '') {
            formData.append(key, petData[key]);
          }
        });
        response = await api.patch(`/pets/${petId}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Send as JSON
        const cleanedData = { ...petData };
        delete cleanedData.photo;
        response = await api.patch(`/pets/${petId}/`, cleanedData);
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message || 'Failed to update pet'
      };
    }
  },

  /**
   * Delete a pet
   * @param {number} petId - Pet ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deletePet(petId) {
    try {
      await api.delete(`/pets/${petId}/`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to delete pet'
      };
    }
  },

  /**
   * Fetch user's category suggestions
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchMySuggestions() {
    try {
      const response = await api.get('/pets/categories/suggest/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch suggestions'
      };
    }
  },

  /**
   * Submit a new category suggestion
   * @param {Object} suggestionData - { suggested_name, description }
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async submitCategorySuggestion(suggestionData) {
    try {
      const response = await api.post('/pets/categories/suggest/', suggestionData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message || 'Failed to submit suggestion'
      };
    }
  },

  /**
   * Fetch user's breed suggestions
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchMyBreedSuggestions() {
    try {
      const response = await api.get('/pets/breeds/suggest/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch breed suggestions'
      };
    }
  },

  /**
   * Submit a new breed suggestion
   * @param {Object} suggestionData - { animal_category, suggested_name, description }
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async submitBreedSuggestion(suggestionData) {
    try {
      const response = await api.post('/pets/breeds/suggest/', suggestionData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message || 'Failed to submit breed suggestion'
      };
    }
  },

  // ============================================
  // ADMIN ENDPOINTS
  // ============================================

  /**
   * Fetch all category suggestions (admin only)
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchAllSuggestions(params = {}) {
    try {
      const response = await api.get('/pets/admin/suggestions/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch suggestions'
      };
    }
  },

  /**
   * Update a category suggestion (admin only)
   * @param {number} suggestionId - Suggestion ID
   * @param {Object} updateData - { status, admin_notes, create_category }
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async updateSuggestion(suggestionId, updateData) {
    try {
      const response = await api.patch(`/pets/admin/suggestions/${suggestionId}/`, updateData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update suggestion'
      };
    }
  },

  /**
   * Fetch all breed suggestions (admin only)
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchAllBreedSuggestions(params = {}) {
    try {
      const response = await api.get('/pets/admin/breed-suggestions/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch breed suggestions'
      };
    }
  },

  /**
   * Update a breed suggestion (admin only)
   * @param {number} suggestionId - Suggestion ID
   * @param {Object} updateData - { status, admin_notes, create_breed }
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async updateBreedSuggestion(suggestionId, updateData) {
    try {
      const response = await api.patch(`/pets/admin/breed-suggestions/${suggestionId}/`, updateData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update breed suggestion'
      };
    }
  },

  // ============================================
  // ADMIN - ANIMAL CATEGORIES
  // ============================================

  /**
   * Fetch all animal categories for admin (includes inactive)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async adminFetchAnimalCategories() {
    try {
      const response = await api.get('/pets/admin/categories/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch animal categories'
      };
    }
  },

  /**
   * Create a new animal category (admin only)
   * @param {Object} categoryData - { name_en, name_el, is_active }
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async adminCreateAnimalCategory(categoryData) {
    try {
      const response = await api.post('/pets/admin/categories/', categoryData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to create animal category'
      };
    }
  },

  /**
   * Update an animal category (admin only)
   * @param {number} categoryId - Category ID
   * @param {Object} categoryData - Updated category data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async adminUpdateAnimalCategory(categoryId, categoryData) {
    try {
      const response = await api.patch(`/pets/admin/categories/${categoryId}/`, categoryData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update animal category'
      };
    }
  },

  /**
   * Delete an animal category (admin only)
   * @param {number} categoryId - Category ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async adminDeleteAnimalCategory(categoryId) {
    try {
      await api.delete(`/pets/admin/categories/${categoryId}/`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to delete animal category'
      };
    }
  },

  // ============================================
  // ADMIN - ALL PETS
  // ============================================

  /**
   * Fetch all pets from all users (admin only)
   * @param {Object} params - Filter params (search, animal_category, size)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async adminFetchAllPets(params = {}) {
    try {
      const response = await api.get('/pets/admin/pets/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch pets'
      };
    }
  },

  // ============================================
  // PREMIUM SUBSCRIPTION
  // ============================================

  /**
   * Get premium subscription status
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getPremiumStatus() {
    try {
      const response = await api.get('/accounts/premium/status/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch premium status'
      };
    }
  },

  /**
   * Start free trial
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async startTrial() {
    try {
      const response = await api.post('/accounts/premium/start-trial/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to start trial'
      };
    }
  },

  /**
   * Create checkout session for subscription
   * @param {Object} options - { success_url, cancel_url }
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async createSubscription(options = {}) {
    try {
      const response = await api.post('/accounts/premium/subscribe/', options);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to create subscription'
      };
    }
  },

  /**
   * Cancel subscription
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async cancelSubscription() {
    try {
      const response = await api.post('/accounts/premium/cancel/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to cancel subscription'
      };
    }
  },

  /**
   * Verify checkout session after payment
   * @param {string} sessionId - Stripe session ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async verifyCheckoutSession(sessionId) {
    try {
      const response = await api.post('/accounts/premium/verify-session/', { session_id: sessionId });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to verify payment'
      };
    }
  },

  // ============================================
  // PET CALENDAR (Premium Feature)
  // ============================================

  /**
   * Fetch pet events
   * @param {Object} params - Query params (pet, event_type, is_completed)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchEvents(params = {}) {
    try {
      const response = await api.get('/pets/calendar/events/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch events'
      };
    }
  },

  /**
   * Create a new pet event
   * @param {Object} eventData - Event data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async createEvent(eventData) {
    try {
      const response = await api.post('/pets/calendar/events/', eventData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message || 'Failed to create event'
      };
    }
  },

  /**
   * Update a pet event
   * @param {number} eventId - Event ID
   * @param {Object} eventData - Updated event data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async updateEvent(eventId, eventData) {
    try {
      const response = await api.patch(`/pets/calendar/events/${eventId}/`, eventData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message || 'Failed to update event'
      };
    }
  },

  /**
   * Delete a pet event
   * @param {number} eventId - Event ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteEvent(eventId) {
    try {
      await api.delete(`/pets/calendar/events/${eventId}/`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to delete event'
      };
    }
  },

  /**
   * Mark event as completed
   * @param {number} eventId - Event ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async completeEvent(eventId) {
    try {
      const response = await api.post(`/pets/calendar/events/${eventId}/complete/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to complete event'
      };
    }
  },

  /**
   * Fetch upcoming events
   * @param {number} days - Number of days to look ahead
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchUpcomingEvents(days = 30) {
    try {
      const response = await api.get('/pets/calendar/upcoming/', { params: { days } });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch upcoming events'
      };
    }
  },

  /**
   * Fetch events for a specific month (calendar view)
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async fetchCalendarMonth(year, month) {
    try {
      const response = await api.get('/pets/calendar/month/', { params: { year, month } });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch calendar'
      };
    }
  }
};

export default petService;
